import { useState, useEffect, useRef, useCallback } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import Spinner from '../components/Spinner';

const getToken = () => localStorage.getItem('ozon_token');
const authHdr  = () => ({ Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' });

async function api(method, path, body) {
  const r = await fetch(`/api${path}`, {
    method, headers: authHdr(),
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || r.statusText);
  return data;
}

function fmtDt(s) {
  if (!s) return '—';
  return new Date(s).toLocaleString('tr-TR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function beep(type = 'ok') {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    const freq = type === 'ok' ? 880 : type === 'dup' ? 440 : 220;
    const dur  = type === 'ok' ? 0.12 : 0.25;
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + dur);
    osc.start(ctx.currentTime); osc.stop(ctx.currentTime + dur);
  } catch { /* sessiz */ }
}

const SUPPORTED_FORMATS = [
  Html5QrcodeSupportedFormats.QR_CODE,
  Html5QrcodeSupportedFormats.CODE_128,
  Html5QrcodeSupportedFormats.CODE_39,
  Html5QrcodeSupportedFormats.EAN_13,
  Html5QrcodeSupportedFormats.EAN_8,
  Html5QrcodeSupportedFormats.DATA_MATRIX,
  Html5QrcodeSupportedFormats.PDF_417,
];

const STATUS_COLOR = {
  delivered:          'text-green-600 bg-green-50',
  cancelled:          'text-red-500 bg-red-50',
  awaiting_packaging: 'text-blue-600 bg-blue-50',
  awaiting_deliver:   'text-purple-600 bg-purple-50',
  ready_to_ship:      'text-indigo-600 bg-indigo-50',
  delivering:         'text-cyan-600 bg-cyan-50',
  kargo_hazir:        'text-teal-600 bg-teal-50',
};

// ── Kamera ────────────────────────────────────────────────────────────────────
function CameraScanner({ onScan, onClose }) {
  const onScanRef = useRef(onScan);
  const [flash, setFlash] = useState(false);
  useEffect(() => { onScanRef.current = onScan; }, [onScan]);

  useEffect(() => {
    const scanner = new Html5Qrcode('need-qr-reader', { formatsToSupport: SUPPORTED_FORMATS, verbose: false });
    scanner.start(
      { facingMode: 'environment' },
      { fps: 25, qrbox: undefined },
      (text) => {
        setFlash(true);
        setTimeout(() => setFlash(false), 300);
        if (navigator.vibrate) navigator.vibrate([80]);
        onScanRef.current(text);
      },
      () => {}
    ).catch(e => console.warn('[NeedScanner]', e));
    return () => { scanner.isScanning && scanner.stop().catch(() => {}); };
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black">
      {flash && <div className="absolute inset-0 bg-green-400/50 z-10 pointer-events-none" />}
      <div className="relative z-20 flex items-center justify-between px-4 py-3 bg-black/70">
        <div>
          <p className="text-white text-sm font-semibold">Etiket Barkodunu Tut</p>
          <p className="text-white/50 text-xs">Okununca yeşil yanar + titreşir</p>
        </div>
        <button onClick={onClose}
          className="text-white bg-white/20 rounded-xl px-4 py-2 text-sm font-semibold active:bg-white/40">
          ✕ Kapat
        </button>
      </div>
      <div id="need-qr-reader" className="flex-1 w-full" />
    </div>
  );
}

// ── Oturum Listesi ────────────────────────────────────────────────────────────
function SessionList({ onSelect, onNew }) {
  const [sessions, setSessions] = useState(null);
  const [loading,  setLoading]  = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setSessions(await api('GET', '/need/sessions')); }
    catch { setSessions([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function del(id, e) {
    e.stopPropagation();
    if (!window.confirm('Bu oturumu silmek istiyor musun?')) return;
    await api('DELETE', `/need/sessions/${id}`);
    load();
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Alım Listesi</h1>
          <p className="text-xs text-gray-400 mt-0.5">Fake ship siparişlerin etiketlerini okut, neye ihtiyaç var gör</p>
        </div>
        <button onClick={onNew}
          className="bg-blue-600 text-white text-sm font-semibold px-4 py-2 rounded-xl hover:bg-blue-700">
          + Yeni Oturum
        </button>
      </div>

      {loading && <div className="flex justify-center py-12"><Spinner size="lg" /></div>}
      {!loading && sessions?.length === 0 && (
        <div className="text-center py-16 text-gray-400">
          <div className="text-4xl mb-3">🛒</div>
          <p className="text-sm font-medium">Henüz oturum yok</p>
          <p className="text-xs mt-1">Yeni oturum aç ve etiketleri okutmaya başla</p>
        </div>
      )}
      <div className="space-y-2">
        {sessions?.map(s => (
          <div key={s.id} onClick={() => onSelect(s)}
            className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm flex items-center gap-3 cursor-pointer hover:border-blue-200 hover:shadow-md transition-all group">
            <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center text-lg flex-shrink-0">🛒</div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-gray-800 text-sm truncate">
                {s.note || fmtDt(s.created_at)}
              </div>
              <div className="text-xs text-gray-400 mt-0.5">
                {s.scan_count} sipariş · {fmtDt(s.created_at)}
              </div>
            </div>
            <button onClick={(e) => del(s.id, e)}
              className="text-gray-200 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all text-lg ml-2">
              ×
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Yeni Oturum Modal ─────────────────────────────────────────────────────────
function NewSessionModal({ onCreated, onClose }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  async function create() {
    setBusy(true);
    try {
      const s = await api('POST', '/need/sessions', { note });
      onCreated(s);
    } catch (e) { alert(e.message); }
    finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 space-y-4">
        <h2 className="font-bold text-gray-800">Yeni Alım Oturumu</h2>
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1.5">Oturum Adı (isteğe bağlı)</label>
          <input value={note} onChange={e => setNote(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && create()}
            placeholder="Örn: 15 Ocak alımı"
            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-blue-400"
            autoFocus />
        </div>
        <div className="flex gap-2">
          <button onClick={onClose}
            className="flex-1 py-2.5 text-sm font-semibold border border-gray-200 text-gray-500 rounded-xl hover:bg-gray-50">
            İptal
          </button>
          <button onClick={create} disabled={busy}
            className="flex-1 py-2.5 text-sm font-semibold bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50">
            {busy ? 'Oluşturuluyor...' : 'Oluştur'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Aktif Oturum (Tarama) ─────────────────────────────────────────────────────
function NeedSession({ session, onBack }) {
  const [events,  setEvents]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [input,   setInput]   = useState('');
  const [busy,    setBusy]    = useState(false);
  const [flash,   setFlash]   = useState(null);
  const [camOpen, setCamOpen] = useState(false);
  const [subTab,  setSubTab]  = useState('scan'); // 'scan' | 'list'
  const [report,  setReport]  = useState([]);
  const [repLoad, setRepLoad] = useState(false);
  const inputRef = useRef(null);

  const loadSession = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api('GET', `/need/sessions/${session.id}`);
      setEvents(d.events || []);
    } catch { setEvents([]); }
    finally { setLoading(false); }
  }, [session.id]);

  const loadReport = useCallback(async () => {
    setRepLoad(true);
    try {
      const d = await api('GET', `/need/sessions/${session.id}/report`);
      setReport(d);
    } catch { setReport([]); }
    finally { setRepLoad(false); }
  }, [session.id]);

  useEffect(() => { loadSession(); }, [loadSession]);
  useEffect(() => {
    if (subTab === 'list') loadReport();
  }, [subTab, loadReport]);

  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 100);
    return () => clearTimeout(t);
  }, []);

  function showFlash(type, msg) {
    setFlash({ type, msg });
    setTimeout(() => setFlash(null), 2500);
  }

  const scan = useCallback(async (pn) => {
    if (!pn.trim() || busy) return;
    setBusy(true);
    try {
      const ev = await api('POST', `/need/sessions/${session.id}/scan`, { posting_number: pn.trim() });
      setEvents(prev => [ev, ...prev]);
      beep('ok');
      showFlash('ok', `✓ Eklendi${ev.order_status ? ` (${ev.order_status})` : ''} — ${ev.items?.length || 0} ürün çeşidi`);
    } catch (e) {
      if (e.message.includes('zaten var')) {
        beep('dup'); showFlash('dup', '⚠ Bu sipariş zaten okutuldu');
      } else {
        beep('err'); showFlash('err', `✗ ${e.message}`);
      }
    } finally {
      setBusy(false); setInput('');
      inputRef.current?.focus();
    }
  }, [session.id, busy]);

  const lastCam = useRef('');
  const handleCamScan = useCallback((decoded) => {
    if (decoded === lastCam.current) return;
    lastCam.current = decoded;
    setTimeout(() => { lastCam.current = ''; }, 3000);
    scan(decoded);
  }, [scan]);

  async function removeEvent(id) {
    await api('DELETE', `/need/events/${id}`);
    setEvents(prev => prev.filter(e => e.id !== id));
  }

  // Toplam ürün özeti (scan tab'ında küçük özet göster)
  const quickSummary = {};
  events.forEach(ev => {
    (ev.items || []).forEach(it => {
      quickSummary[it.offer_id] = (quickSummary[it.offer_id] || 0) + it.quantity;
    });
  });
  const summaryEntries = Object.entries(quickSummary).sort((a, b) => b[1] - a[1]);

  const flashColors = {
    ok:  'bg-green-50 border-green-300 text-green-800',
    dup: 'bg-orange-50 border-orange-300 text-orange-800',
    err: 'bg-red-50 border-red-300 text-red-700',
  };

  return (
    <div className="space-y-3">
      {camOpen && <CameraScanner onScan={handleCamScan} onClose={() => setCamOpen(false)} />}

      {/* Başlık */}
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="text-gray-400 hover:text-gray-700 text-xl">‹</button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="font-bold text-gray-900 truncate">{session.note || fmtDt(session.created_at)}</h1>
            <span className="text-xs bg-green-50 text-green-600 px-2 py-0.5 rounded-full font-semibold flex-shrink-0">
              {events.length} sipariş
            </span>
          </div>
          <p className="text-xs text-gray-300">{fmtDt(session.created_at)}</p>
        </div>
      </div>

      {/* Sub tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl">
        {[['scan','📷 Tarama'],['list','🛒 Alım Listesi']].map(([k,l]) => (
          <button key={k} onClick={() => setSubTab(k)}
            className={`flex-1 py-2 text-xs font-semibold rounded-lg transition-colors ${subTab === k ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {l}
          </button>
        ))}
      </div>

      {/* TARAMA TABI */}
      {subTab === 'scan' && (
        <>
          {flash && (
            <div className={`border rounded-xl px-4 py-2.5 text-sm font-medium ${flashColors[flash.type]}`}>
              {flash.msg}
            </div>
          )}

          {/* Barkod input */}
          <div className="bg-white border-2 border-blue-200 rounded-2xl p-4 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-blue-600">Etiket Barkodu / QR Okut</label>
              <button onClick={() => setCamOpen(true)}
                className="flex items-center gap-1.5 bg-blue-600 text-white text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-blue-700">
                📷 Kamera
              </button>
            </div>
            <div className="flex gap-2">
              <input ref={inputRef} value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && scan(input)}
                placeholder="Sipariş numarası..."
                className="flex-1 border border-gray-200 rounded-xl px-3 py-3 text-sm font-mono focus:outline-none focus:border-blue-400"
                autoComplete="off" autoCorrect="off" spellCheck={false} />
              <button onClick={() => scan(input)} disabled={busy || !input.trim()}
                className="px-4 py-3 bg-blue-600 text-white rounded-xl font-semibold text-sm hover:bg-blue-700 disabled:opacity-40">
                {busy ? '...' : 'Okut'}
              </button>
            </div>
          </div>

          {/* Hızlı özet */}
          {summaryEntries.length > 0 && (
            <div className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">
                Anlık İhtiyaç — {summaryEntries.length} çeşit, {summaryEntries.reduce((s,[,q]) => s+q, 0)} adet
              </p>
              <div className="flex flex-wrap gap-1.5">
                {summaryEntries.map(([offer_id, qty]) => (
                  <span key={offer_id}
                    className="text-xs bg-orange-50 text-orange-700 px-2.5 py-1 rounded-lg font-semibold">
                    {qty}× {offer_id}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Taranan liste */}
          {loading && <div className="flex justify-center py-8"><Spinner size="md" /></div>}
          {!loading && events.length === 0 && (
            <div className="text-center py-10 text-gray-400">
              <div className="text-3xl mb-2">🔍</div>
              <p className="text-sm">Henüz tarama yok</p>
              <p className="text-xs mt-1">Kargodaki siparişlerin etiketlerini okut</p>
            </div>
          )}
          {!loading && events.length > 0 && (
            <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm">
              <div className="px-4 py-3 border-b border-gray-50 flex justify-between items-center">
                <span className="text-xs font-semibold text-gray-500 uppercase">Taranan Siparişler</span>
                <span className="text-xs text-gray-400">{events.length} adet</span>
              </div>
              <div className="divide-y divide-gray-50 max-h-[50vh] overflow-y-auto">
                {events.map((ev, i) => (
                  <div key={ev.id} className="px-4 py-3 flex items-start gap-3 group">
                    <span className="text-xs text-gray-300 w-5 text-right flex-shrink-0 mt-0.5">{events.length - i}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs font-semibold text-gray-700">{ev.posting_number}</span>
                        {ev.order_status && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${STATUS_COLOR[ev.order_status] || 'text-gray-500 bg-gray-50'}`}>
                            {ev.order_status}
                          </span>
                        )}
                      </div>
                      {ev.items?.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {ev.items.map(it => (
                            <span key={it.offer_id} className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded font-medium">
                              {it.quantity}× {it.offer_id}
                            </span>
                          ))}
                        </div>
                      )}
                      <p className="text-[10px] text-gray-300 mt-1">{fmtDt(ev.scanned_at)}</p>
                    </div>
                    <button onClick={() => removeEvent(ev.id)}
                      className="text-gray-200 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all text-lg flex-shrink-0 mt-0.5">
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* ALİM LİSTESİ TABI */}
      {subTab === 'list' && (
        <NeedReport
          sessionId={session.id}
          report={report}
          loading={repLoad}
          onRefresh={loadReport}
        />
      )}
    </div>
  );
}

// ── Alım Listesi Raporu ───────────────────────────────────────────────────────
function NeedReport({ sessionId, report, loading, onRefresh }) {
  const [expanded,  setExpanded]  = useState(null); // offer_id
  const [updating,  setUpdating]  = useState(null);
  const [filterDone, setFilterDone] = useState(false);

  async function toggleOrdered(item) {
    setUpdating(item.offer_id);
    try {
      const newQty = item.ordered_qty >= item.total_qty ? 0 : item.total_qty;
      await api('PATCH', `/need/sessions/${sessionId}/ordered`, {
        offer_id: item.offer_id,
        ordered_qty: newQty,
      });
      await onRefresh();
    } catch (e) { alert(e.message); }
    finally { setUpdating(null); }
  }

  async function setOrderedQty(item, qty) {
    setUpdating(item.offer_id);
    try {
      await api('PATCH', `/need/sessions/${sessionId}/ordered`, {
        offer_id: item.offer_id,
        ordered_qty: qty,
      });
      await onRefresh();
    } catch (e) { alert(e.message); }
    finally { setUpdating(null); }
  }

  function downloadCSV() {
    const lines = [['Ürün Kodu','Ürün Adı','İhtiyaç','Sipariş Edildi','Kalan','Durum'].join(';')];
    report.forEach(r => {
      const kalan = r.total_qty - (r.ordered_qty || 0);
      lines.push([
        r.offer_id,
        r.product_name || '',
        r.total_qty,
        r.ordered_qty || 0,
        kalan,
        r.ordered_qty >= r.total_qty ? 'Tamam' : 'Bekliyor',
      ].join(';'));
    });
    const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `alim-listesi-${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  }

  const shown    = filterDone ? report.filter(r => (r.ordered_qty || 0) < r.total_qty) : report;
  const doneCount = report.filter(r => (r.ordered_qty || 0) >= r.total_qty).length;
  const totalItems = report.reduce((s, r) => s + r.total_qty, 0);
  const orderedItems = report.reduce((s, r) => s + (r.ordered_qty || 0), 0);

  if (loading) return <div className="flex justify-center py-12"><Spinner size="lg" /></div>;

  if (report.length === 0) return (
    <div className="text-center py-16 text-gray-400">
      <div className="text-4xl mb-3">🛒</div>
      <p className="text-sm">Henüz ürün yok</p>
      <p className="text-xs mt-1">Tarama sekmesinden sipariş okut</p>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Özet bar */}
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-white border border-gray-100 rounded-xl p-3 text-center shadow-sm">
          <div className="text-xl font-bold text-gray-800">{report.length}</div>
          <div className="text-[11px] text-gray-400 mt-0.5">Ürün çeşidi</div>
        </div>
        <div className="bg-white border border-gray-100 rounded-xl p-3 text-center shadow-sm">
          <div className="text-xl font-bold text-orange-600">{totalItems - orderedItems}</div>
          <div className="text-[11px] text-gray-400 mt-0.5">Sipariş bekliyor</div>
        </div>
        <div className="bg-white border border-gray-100 rounded-xl p-3 text-center shadow-sm">
          <div className="text-xl font-bold text-green-600">{doneCount}</div>
          <div className="text-[11px] text-gray-400 mt-0.5">Tamamlandı</div>
        </div>
      </div>

      {/* İlerleme */}
      {totalItems > 0 && (
        <div className="bg-white border border-gray-100 rounded-xl p-3 shadow-sm">
          <div className="flex justify-between text-xs text-gray-500 mb-1.5">
            <span>İlerleme</span>
            <span>{orderedItems} / {totalItems} adet</span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2">
            <div className="bg-green-500 h-2 rounded-full transition-all"
              style={{ width: `${Math.min(100, (orderedItems / totalItems) * 100)}%` }} />
          </div>
        </div>
      )}

      {/* Kontroller */}
      <div className="flex gap-2 flex-wrap items-center">
        <button onClick={() => setFilterDone(f => !f)}
          className={`px-3 py-1.5 text-xs font-semibold rounded-xl border transition-colors ${
            filterDone ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:border-blue-200'
          }`}>
          {filterDone ? '✓ Sadece bekleyenler' : 'Sadece bekleyenleri göster'}
        </button>
        <button onClick={downloadCSV}
          className="px-3 py-1.5 text-xs font-semibold rounded-xl border border-gray-200 bg-white text-gray-600 hover:border-gray-300">
          CSV İndir
        </button>
        <button onClick={onRefresh}
          className="px-3 py-1.5 text-xs font-semibold rounded-xl border border-gray-200 bg-white text-gray-600 hover:border-gray-300">
          ↻ Yenile
        </button>
      </div>

      {/* Alışveriş listesi */}
      <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm">
        <div className="px-4 py-3 border-b border-gray-50 text-xs font-semibold text-gray-500 uppercase">
          {shown.length} ürün
        </div>
        <div className="divide-y divide-gray-50">
          {shown.map(item => {
            const done    = (item.ordered_qty || 0) >= item.total_qty;
            const partial = (item.ordered_qty || 0) > 0 && !done;
            const isExp   = expanded === item.offer_id;

            return (
              <div key={item.offer_id}>
                <div className={`px-4 py-3 flex items-center gap-3 transition-colors ${done ? 'bg-green-50/40' : ''}`}>
                  {/* Checkbox */}
                  <button
                    onClick={() => toggleOrdered(item)}
                    disabled={updating === item.offer_id}
                    className={`w-6 h-6 rounded-lg border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
                      done    ? 'bg-green-500 border-green-500 text-white'
                      : partial ? 'bg-orange-100 border-orange-400'
                      : 'border-gray-300 hover:border-blue-400'
                    }`}>
                    {done ? '✓' : partial ? '~' : ''}
                  </button>

                  {/* Ürün bilgisi */}
                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setExpanded(isExp ? null : item.offer_id)}>
                    <div className={`text-sm font-semibold truncate ${done ? 'line-through text-gray-400' : 'text-gray-800'}`}>
                      {item.offer_id}
                    </div>
                    {item.product_name && (
                      <div className="text-[11px] text-gray-400 truncate">{item.product_name}</div>
                    )}
                    <div className="text-[11px] text-gray-400 mt-0.5">
                      {item.order_count} sipariş
                    </div>
                  </div>

                  {/* Miktar */}
                  <div className="text-right flex-shrink-0">
                    <div className={`text-lg font-bold ${done ? 'text-green-600' : 'text-orange-600'}`}>
                      {item.total_qty}
                    </div>
                    <div className="text-[10px] text-gray-400">adet</div>
                  </div>

                  {/* Sipariş edilen miktar input */}
                  <div className="flex-shrink-0 w-16">
                    <input
                      type="number" min="0" max={item.total_qty}
                      value={item.ordered_qty || ''}
                      onChange={e => setOrderedQty(item, parseInt(e.target.value) || 0)}
                      placeholder="0"
                      className={`w-full border rounded-lg px-2 py-1 text-xs text-center focus:outline-none focus:border-blue-400 ${
                        done ? 'border-green-300 bg-green-50 text-green-700' : 'border-gray-200'
                      }`}
                    />
                    <div className="text-[9px] text-gray-400 text-center mt-0.5">sipariş</div>
                  </div>

                  {/* Expand arrow */}
                  <button onClick={() => setExpanded(isExp ? null : item.offer_id)}
                    className="text-gray-300 hover:text-gray-500 flex-shrink-0 text-sm">
                    {isExp ? '▲' : '▼'}
                  </button>
                </div>

                {/* Sipariş detayı (açıldığında) */}
                {isExp && (
                  <div className="px-4 pb-3 bg-gray-50/50">
                    <div className="text-[10px] font-semibold text-gray-400 uppercase mb-1.5 pt-2">
                      Hangi siparişlerde
                    </div>
                    <div className="space-y-1">
                      {item.orders?.map((o, i) => (
                        <div key={i} className="flex items-center gap-2 text-[11px]">
                          <span className="font-mono text-gray-600">{o.posting_number}</span>
                          <span className={`px-1.5 py-0.5 rounded font-medium ${STATUS_COLOR[o.order_status] || 'text-gray-500 bg-gray-100'}`}>
                            {o.order_status || '?'}
                          </span>
                          <span className="ml-auto font-semibold text-gray-700">{o.qty} adet</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Ana Sayfa ─────────────────────────────────────────────────────────────────
export default function NeedScan() {
  const [view,    setView]    = useState('list');
  const [session, setSession] = useState(null);

  return (
    <div className="p-3 sm:p-6 max-w-4xl mx-auto">
      {view === 'list' && (
        <SessionList
          onSelect={s => { setSession(s); setView('session'); }}
          onNew={() => setView('new')}
        />
      )}
      {view === 'new' && (
        <>
          <SessionList onSelect={s => { setSession(s); setView('session'); }} onNew={() => {}} />
          <NewSessionModal
            onCreated={s => { setSession(s); setView('session'); }}
            onClose={() => setView('list')}
          />
        </>
      )}
      {view === 'session' && session && (
        <NeedSession session={session} onBack={() => setView('list')} />
      )}
    </div>
  );
}
